// Package Imports
import { isDateInSpecifiedRange } from '@/lib/functions/isDateInSpecifiedRange';

/**
 * Determines the lowest active single-unit price and the type of discount that applied.
 *
 * - If the key club discount is active, it's prioritized.
 * - If not, but a promo discount is active, it’s used.
 * - If no discounts apply, the regular price is returned.
 * - Also returns the raw keyClubDiscount for UI display even if not applied.
 */
export const calculateUnitPrice = ({
  price,
  promoPrice,
  promoPriceStartDate,
  promoPriceEndDate,
  keyClubDiscount,
  keyClubDiscountStartDate,
  keyClubDiscountEndDate,
}: {
  price?: number | null;
  promoPrice?: number | null;
  promoPriceStartDate?: string | null;
  promoPriceEndDate?: string | null;
  keyClubDiscount?: number | null;
  keyClubDiscountStartDate?: string | null;
  keyClubDiscountEndDate?: string | null;
}): {
  unitPrice: number | null;
  unitPriceKeyclubDiscount: number | null;
  unitPricePromoDiscount: number | null;
} => {
  const now = new Date();

  if (price == null) {
    return {
      unitPrice: null,
      unitPriceKeyclubDiscount: null,
      unitPricePromoDiscount: null,
    };
  }

  // Key Club applies if it's > 0, less than base, and within valid range
  const keyClubValid =
    keyClubDiscount != null &&
    keyClubDiscount > 0 &&
    isDateInSpecifiedRange(now, keyClubDiscountStartDate, keyClubDiscountEndDate);

  // Promo applies if not null, > 0, and within active date range
  const promoValid =
    promoPrice != null &&
    promoPrice > 0 &&
    isDateInSpecifiedRange(now, promoPriceStartDate, promoPriceEndDate);

  // Calculate the effective promo price discount
  const unitPricePromoDiscount =
    typeof price === 'number' &&
    typeof promoPrice === 'number' &&
    price > promoPrice
      ? price - promoPrice
      : null;

  // Return the unit pricing details
  return {
    unitPrice: price,
    unitPriceKeyclubDiscount: keyClubValid ? keyClubDiscount : null,
    unitPricePromoDiscount: promoValid ? unitPricePromoDiscount : null,
  };
};
